import React, {ChangeEvent, useLayoutEffect, useRef, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Debouncer} from '@common/debouncer'
import {keyboardHandler} from '@containers/hooks'
import {cx} from '@containers/html-utils'
import SearchIcon from './search.svg'
import {InputProps, InputValidationType, TextInputProps} from './types'

type TwInputContainerProps = {
  $hasError?: boolean
  $isSuccess?: boolean
  $disabled?: boolean
  $large?: boolean
  $searchIcon?: boolean
}
const TwInputContainer = tw.div<TwInputContainerProps>`
    flex
    items-center
    rounded-4
    border
    border-solid
    ${p => (p.$searchIcon ? 'pl-4' : 'pl-8')}
    pr-8
    outline-none
    ${p =>
      p.$hasError
        ? cx`
        border-error-old
    `
        : p.$isSuccess
        ? cx`
        border-success-old
        `
        : cx`
        border-main-old
        text-obscure-old
        focus-within:border-action-default
        focus-within:text-action-default
    `}
    ${p => (p.$disabled ? 'bg-input-disabled-old' : 'bg-input-old')}
    ${p => (p.$large ? 'h-32 text-14' : 'h-24 text-12')}
`

type TwInputProps = {
  $color?: string
}
const TwInput = tw.input<TwInputProps>`
  p-0
  bg-input-old
  border-none
  outline-none
  w-full
  text-main-old
`

export const TextInput = (props: TextInputProps) => {
  const debouncer = useRef(
    props.debounceMs ? new Debouncer(props.debounceMs) : undefined
  )
  const [value, setValue] = useState(props.value)
  const [validationType, setValidationType] =
    useState<InputValidationType>(undefined)
  useLayoutEffect(() => {
    setValue(props.value)
    if (props.isValid) {
      setValidationType(props.isValid(props.value))
    }
    return () => debouncer.current?.clear()
  }, [props.value])
  const onChange = (value: string) => {
    setValue(value)
    if (debouncer.current) {
      debouncer.current.runLatest(() => props.onChange(value))
    } else {
      props.onChange(value)
    }
  }

  const proxyKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (props.onKeyUp) {
      props.onKeyUp(e)
    }

    void keyboardHandler({
      enter: props.onEnter,
      escape: props.onEscape
    })(e)
  }

  return (
    <TwInputContainer
      data-dest="sticker-container-input"
      $hasError={props.isValid ? validationType === 'error' : props.hasError}
      $isSuccess={
        props.isValid ? validationType === 'success' : props.isCorrect
      }
      $disabled={props.disabled}
      $large={props.large}
      $searchIcon={props.searchIcon}
      className={props.className}
      style={props.inputContainerStyle}
    >
      {props.searchIcon && <SearchIcon className="shrink-0 mr-4" />}
      <TwInput
        data-test="stiker-container-input-field"
        placeholder={props.placeholder}
        disabled={props.disabled}
        value={value}
        onChange={x => onChange(x.target.value)}
        onKeyUp={proxyKeyUp}
        onClick={props?.onClick}
        autoFocus={props.autoFocus ?? false}
        ref={props.innerRef}
        type={props.type}
        style={{...props.style}}
      />
    </TwInputContainer>
  )
}

export type NumberInputProps = InputProps<number | undefined>

export const NumberInput = (props: NumberInputProps) => {
  const [error, setError] = useState(false)

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(event.target.value.replace(/\D/g, ''), 10)
    setError(isNaN(newValue))
    props.onChange(isNaN(newValue) ? undefined : newValue)
  }

  return (
    <TwInputContainer
      $hasError={props.hasError || error}
      $disabled={props.disabled}
      $large={props.large}
      $searchIcon={false}
      className={props.className}
    >
      <TwInput
        className="text-right"
        placeholder={props.placeholder}
        disabled={props.disabled}
        value={props.value ?? ''}
        onChange={onChange}
        autoFocus={props.autoFocus ?? false}
      />
    </TwInputContainer>
  )
}
